import React from 'react';
import { Select, PhoneInput } from '../../../components';
import type { SelectOption } from '../../../components';
import {
  PersonIcon,
  EnvelopeClosedIcon,
  ArchiveIcon,
  BookmarkIcon,
  MixIcon,
  CalendarIcon,
} from '@radix-ui/react-icons';
import { LeadFormData } from '../leadFormUtils';
import { LeadStatus } from '../../../types/lead';

const STATUS_OPTIONS: SelectOption[] = [
  { value: 'new_lead', label: 'New Lead' },
  { value: 'lead', label: 'Lead' },
  { value: 'progress', label: 'In Progress' },
  { value: 'qualified', label: 'Qualified' },
  { value: 'pending', label: 'Pending' },
];

const GENDER_OPTIONS: SelectOption[] = [
  { value: 'Male', label: 'Male' },
  { value: 'Female', label: 'Female' },
];

const LOOKING_FOR_OPTIONS: SelectOption[] = [
  { value: 'Male', label: 'Male' },
  { value: 'Female', label: 'Female' },
];

const STATUS_LABELS: Record<LeadStatus, string> = {
  new_lead: 'New Lead',
  lead: 'Lead',
  progress: 'In Progress',
  qualified: 'Qualified',
  pending: 'Pending',
  converted: 'Converted',
};

export interface LeadCustomerDetailsSectionProps {
  formData: LeadFormData;
  sourceOptions: SelectOption[];
  emailError: string | null | undefined;
  phoneError: string | null;
  isStatusReadOnly: boolean;
  onFieldChange: <K extends keyof LeadFormData>(field: K, value: LeadFormData[K]) => void;
  onEmailChange: (value: string) => void;
  onEmailBlur: (value: string) => void;
  onPhoneChange: (value: string) => void;
  onPhoneBlur: () => void;
}

export const LeadCustomerDetailsSection: React.FC<LeadCustomerDetailsSectionProps> = ({
  formData,
  sourceOptions,
  emailError,
  phoneError,
  isStatusReadOnly,
  onFieldChange,
  onEmailChange,
  onEmailBlur,
  onPhoneChange,
  onPhoneBlur,
}) => (
  <section className="lead-form-shared__section">
    <h3 className="lead-form-shared__section-title">Customer details</h3>

    <div className="lead-form-modal__row">
      <div className="lead-form-modal__field">
        <label className="lead-form-modal__label" htmlFor="lead-date">
          Date
        </label>
        <div className="lead-form-modal__input-wrap">
          <CalendarIcon className="lead-form-modal__input-icon" aria-hidden="true" />
          <input
            id="lead-date"
            type="date"
            className="lead-form-modal__input"
            value={formData.leadDate}
            onChange={(e) => onFieldChange('leadDate', e.target.value)}
          />
        </div>
      </div>

      <div className="lead-form-modal__field">
        <label className="lead-form-modal__label">
          Source <span className="lead-form-modal__required">*</span>
        </label>
        <div className="lead-form-modal__select-wrap">
          <ArchiveIcon className="lead-form-modal__input-icon" aria-hidden="true" />
          <Select
            placeholder="Select Source"
            value={formData.leadSourceId}
            onChange={(value) => onFieldChange('leadSourceId', value)}
            options={sourceOptions}
            searchable={false}
            className="lead-form-modal__select"
          />
        </div>
      </div>
    </div>

    <div className="lead-form-modal__row">
      <div className="lead-form-modal__field">
        <label className="lead-form-modal__label" htmlFor="lead-counsellor">
          Counsellor
        </label>
        <div className="lead-form-modal__input-wrap">
          <PersonIcon className="lead-form-modal__input-icon" aria-hidden="true" />
          <input
            id="lead-counsellor"
            type="text"
            className="lead-form-modal__input"
            placeholder="Counsellor name"
            value={formData.counsellorName}
            onChange={(e) => onFieldChange('counsellorName', e.target.value)}
          />
        </div>
      </div>

      <div className="lead-form-modal__field">
        <label className="lead-form-modal__label" htmlFor="lead-full-name">
          Name <span className="lead-form-modal__required">*</span>
        </label>
        <div className="lead-form-modal__input-wrap">
          <PersonIcon className="lead-form-modal__input-icon" aria-hidden="true" />
          <input
            id="lead-full-name"
            type="text"
            className="lead-form-modal__input"
            placeholder="Jane Doe"
            value={formData.fullName}
            onChange={(e) => onFieldChange('fullName', e.target.value)}
          />
        </div>
      </div>
    </div>

    <div className="lead-form-modal__row">
      <div className="lead-form-modal__field">
        <label className="lead-form-modal__label" htmlFor="lead-email">
          Email <span className="lead-form-modal__required">*</span>
        </label>
        <div className="lead-form-modal__input-wrap">
          <EnvelopeClosedIcon className="lead-form-modal__input-icon" aria-hidden="true" />
          <input
            id="lead-email"
            type="email"
            className={`lead-form-modal__input${emailError ? ' lead-form-modal__input--error' : ''}`}
            placeholder="jane@example.com"
            value={formData.email}
            onChange={(e) => {
              onFieldChange('email', e.target.value);
              onEmailChange(e.target.value);
            }}
            onBlur={() => onEmailBlur(formData.email)}
            aria-invalid={emailError ? true : undefined}
          />
        </div>
        {emailError && (
          <span className="lead-form-modal__field-error" role="alert">
            {emailError}
          </span>
        )}
      </div>

      <div className="lead-form-modal__field lead-form-modal__field--phone">
        <PhoneInput
          label="Contact Number"
          value={formData.phoneE164 || undefined}
          onChange={onPhoneChange}
          onBlur={onPhoneBlur}
          error={phoneError ?? undefined}
          placeholder="9876543210"
          defaultCountry="IN"
          international
          className="lead-form-modal__phone-input"
        />
      </div>
    </div>

    <div className="lead-form-modal__row">
      <div className="lead-form-modal__field">
        <label className="lead-form-modal__label">Gender</label>
        <div className="lead-form-modal__select-wrap">
          <MixIcon className="lead-form-modal__input-icon" aria-hidden="true" />
          <Select
            placeholder="Select Gender"
            value={formData.gender}
            onChange={(value) => onFieldChange('gender', value)}
            options={GENDER_OPTIONS}
            searchable={false}
            className="lead-form-modal__select"
          />
        </div>
      </div>

      <div className="lead-form-modal__field">
        <label className="lead-form-modal__label" htmlFor="lead-age">
          Age
        </label>
        <input
          id="lead-age"
          type="text"
          className="lead-form-modal__input lead-form-modal__input--plain"
          placeholder="28"
          value={formData.age}
          onChange={(e) => onFieldChange('age', e.target.value)}
        />
      </div>
    </div>

    <div className="lead-form-modal__row">
      <div className="lead-form-modal__field">
        <label className="lead-form-modal__label" htmlFor="lead-qualification">
          Qualification
        </label>
        <input
          id="lead-qualification"
          type="text"
          className="lead-form-modal__input lead-form-modal__input--plain"
          placeholder="MBA"
          value={formData.qualification}
          onChange={(e) => onFieldChange('qualification', e.target.value)}
        />
      </div>

      <div className="lead-form-modal__field">
        <label className="lead-form-modal__label" htmlFor="lead-occupation">
          Occupation
        </label>
        <input
          id="lead-occupation"
          type="text"
          className="lead-form-modal__input lead-form-modal__input--plain"
          placeholder="Engineer"
          value={formData.occupation}
          onChange={(e) => onFieldChange('occupation', e.target.value)}
        />
      </div>
    </div>

    <div className="lead-form-modal__row">
      <div className="lead-form-modal__field">
        <label className="lead-form-modal__label" htmlFor="lead-caste">
          Caste
        </label>
        <input
          id="lead-caste"
          type="text"
          className="lead-form-modal__input lead-form-modal__input--plain"
          value={formData.caste}
          onChange={(e) => onFieldChange('caste', e.target.value)}
        />
      </div>

      <div className="lead-form-modal__field">
        <label className="lead-form-modal__label" htmlFor="lead-height">
          Height
        </label>
        <input
          id="lead-height"
          type="text"
          className="lead-form-modal__input lead-form-modal__input--plain"
          placeholder={"5'6\""}
          value={formData.height}
          onChange={(e) => onFieldChange('height', e.target.value)}
        />
      </div>
    </div>

    <div className="lead-form-modal__row">
      <div className="lead-form-modal__field">
        <label className="lead-form-modal__label" htmlFor="lead-marital-status">
          Marital Status
        </label>
        <input
          id="lead-marital-status"
          type="text"
          className="lead-form-modal__input lead-form-modal__input--plain"
          placeholder="Single"
          value={formData.maritalStatus}
          onChange={(e) => onFieldChange('maritalStatus', e.target.value)}
        />
      </div>

      <div className="lead-form-modal__field">
        <label className="lead-form-modal__label" htmlFor="lead-divorce-info">
          If Divorce
        </label>
        <input
          id="lead-divorce-info"
          type="text"
          className="lead-form-modal__input lead-form-modal__input--plain"
          value={formData.divorceInfo}
          onChange={(e) => onFieldChange('divorceInfo', e.target.value)}
        />
      </div>
    </div>

    <div className="lead-form-modal__field lead-form-shared__field--full">
      <label className="lead-form-modal__label" htmlFor="lead-location">
        City | State | Country
      </label>
      <input
        id="lead-location"
        type="text"
        className="lead-form-modal__input lead-form-modal__input--plain"
        placeholder="Mumbai, Maharashtra, India"
        value={formData.location}
        onChange={(e) => onFieldChange('location', e.target.value)}
      />
    </div>

    <div className="lead-form-modal__field lead-form-shared__field--full">
      <label className="lead-form-modal__label" htmlFor="lead-requirements">
        Requirements
      </label>
      <textarea
        id="lead-requirements"
        className="lead-form-modal__textarea"
        placeholder="Looking for professional match"
        rows={3}
        value={formData.requirements}
        onChange={(e) => onFieldChange('requirements', e.target.value)}
      />
    </div>

    <div className="lead-form-modal__row">
      <div className="lead-form-modal__field">
        <label className="lead-form-modal__label">Status</label>
        {isStatusReadOnly ? (
          <span className="lead-form-shared__status-badge">
            {STATUS_LABELS[formData.status]}
          </span>
        ) : (
          <div className="lead-form-modal__select-wrap">
            <BookmarkIcon className="lead-form-modal__input-icon" aria-hidden="true" />
            <Select
              value={formData.status === 'converted' ? 'qualified' : formData.status}
              onChange={(value) => onFieldChange('status', value as LeadStatus)}
              options={STATUS_OPTIONS}
              searchable={false}
              className="lead-form-modal__select"
            />
          </div>
        )}
      </div>

      <div className="lead-form-modal__field">
        <label className="lead-form-modal__label">Looking For</label>
        <div className="lead-form-modal__select-wrap">
          <PersonIcon className="lead-form-modal__input-icon" aria-hidden="true" />
          <Select
            placeholder="Select Option"
            value={formData.preferredGender}
            onChange={(value) => onFieldChange('preferredGender', value)}
            options={LOOKING_FOR_OPTIONS}
            searchable={false}
            className="lead-form-modal__select"
          />
        </div>
      </div>
    </div>
  </section>
);
